// Onboarding analytics API (CWA-159, spec 009).
//
// Eleven read-only endpoints under /admin/prospects/dashboard on acc-management,
// all admin-gated (accountApi attaches the Bearer token via its interceptor).
//
// Every function maps the snake_case response onto the camelCase domain type
// here, at the boundary, so nothing downstream sees a wire key. The full
// contract — including the parameters the API validates and the four defects it
// currently carries — is in
// specs/009-onboarding-analytics-dashboard/contracts/dashboard-api.md.

import { accountApi } from "../../config/apiConfig";
import {
  CustomerTypeSegmentation,
  DashboardKpis,
  FunnelStage,
  LeadSourceStat,
  MonthlyDuration,
  MonthlyVolume,
  OnboardingSatisfaction,
  OverdueAlert,
  ProspectAnalyticsPage,
  ProspectAnalyticsRow,
  ProspectListQuery,
  SignMethodStat,
  STAR_VALUES,
  StarValue,
  StepDuration,
  VendorStat,
} from "../../types/onboardingAnalytics";
import { ProspectTypeFilter } from "../../types/onboarding";

const BASE = "admin/prospects/dashboard";

// CWA-184: which onboardings a section counts. Sent only when chosen, so a call without it stays exactly as it
// was and the backend's default (new customers only) applies.
const getSection = (
  path: string,
  params: Record<string, unknown> = {},
  prospectType?: ProspectTypeFilter
) => {
  const all = prospectType ? { ...params, prospect_type: prospectType } : params;
  return Object.keys(all).length > 0
    ? accountApi.get(path, { params: all })
    : accountApi.get(path);
};

// The separator the API builds its transition strings with. Split here rather
// than in a component: it is a property of the wire format, not of any screen.
const TRANSITION_SEPARATOR = "→";

// GET /kpis — the nine headline figures.
//
// `period` is deliberately NOT sent. The endpoint accepts and validates it, then
// never reads it: the response always carries both the weekly and the monthly
// counts. Sending it would imply a scoping that does not happen (defect 4).
export const getDashboardKpis = async (
  prospectType?: ProspectTypeFilter
): Promise<DashboardKpis> => {
  const { data } = await getSection(`${BASE}/kpis`, {}, prospectType);
  return {
    totalInPipeline: data.total_in_pipeline,
    totalCustomersLive: data.total_customers_live,
    newProspectsThisWeek: data.new_prospects_this_week,
    newProspectsThisMonth: data.new_prospects_this_month,
    newCustomersThisWeek: data.new_customers_this_week,
    newCustomersThisMonth: data.new_customers_this_month,
    overallConversionRatePct: data.overall_conversion_rate_pct,
    avgOnboardingDurationDays: data.avg_onboarding_duration_days,
    overdueCount: data.overdue_count,
  };
};

// GET /funnel — count per stage with the loss since the stage above.
//
// `label_de` is dropped rather than carried: it is the only label the API
// supplies, it is German, and this Back Office is English-only. Stage names come
// from our own state labels instead (FR-030).
export const getFunnel = async (
  prospectType?: ProspectTypeFilter
): Promise<FunnelStage[]> => {
  const { data } = await getSection(`${BASE}/funnel`, {}, prospectType);
  return (data?.stages ?? []).map((s: any) => ({
    state: s.state,
    count: s.count,
    dropOff: s.drop_off ?? null,
    dropOffPct: s.drop_off_pct ?? null,
  }));
};

// GET /time-per-step — average days per recorded adjacent transition.
//
// The API sends `transition: "FROM → TO"`. A value that does not split into two
// non-empty halves is dropped rather than rendered: it would put a raw combined
// identifier on screen, which is exactly what must not happen (FR-031).
export const getTimePerStep = async (
  prospectType?: ProspectTypeFilter
): Promise<StepDuration[]> => {
  const { data } = await getSection(`${BASE}/time-per-step`, {}, prospectType);
  return (data?.steps ?? [])
    .map((s: any) => {
      const parts = String(s.transition ?? "").split(TRANSITION_SEPARATOR);
      if (parts.length !== 2) return null;
      const from = parts[0].trim();
      const to = parts[1].trim();
      if (!from || !to) return null;
      return { from, to, avgDays: s.avg_days };
    })
    .filter((s: StepDuration | null): s is StepDuration => s !== null);
};

// GET /duration-trend?months=N — average total onboarding duration per month.
export const getDurationTrend = async (
  months: number,
  prospectType?: ProspectTypeFilter
): Promise<MonthlyDuration[]> => {
  const { data } = await getSection(`${BASE}/duration-trend`, { months }, prospectType);
  return (data?.months ?? []).map((m: any) => ({
    month: m.month,
    avgDays: m.avg_days,
    count: m.count,
  }));
};

// GET /sign-method-breakdown — days from contract sent to signed, per method.
export const getSignMethodBreakdown = async (
  prospectType?: ProspectTypeFilter
): Promise<SignMethodStat[]> => {
  const { data } = await getSection(`${BASE}/sign-method-breakdown`, {}, prospectType);
  return (data?.methods ?? []).map((m: any) => ({
    method: m.method,
    avgDays: m.avg_days,
    count: m.count,
  }));
};

const mapProspectRow = (p: any): ProspectAnalyticsRow => ({
  id: p.id,
  fullName: p.full_name,
  customerType: p.customer_type,
  state: p.state,
  leadSource: p.lead_source ?? null,
  vendor: p.vendor,
  signMethod: p.sign_method ?? null,
  createdAt: p.created_at,
  lastChangedAt: p.last_changed_at ?? null,
  daysInState: p.days_in_state,
  daysTotal: p.days_total,
  lastActor: p.last_actor ?? null,
  setupFeeStatus: p.setup_fee_status ?? null,
});

// GET /list — the paged, filtered, sorted prospect table.
//
// Only parameters the caller actually set are sent, so the API's own defaults
// apply otherwise. `state` repeats for a multi-select; axios serialises an array
// as repeated keys, which is the shape the endpoint reads.
//
// There is deliberately no CSV variant here: `format=csv` re-queries with an
// unbounded page size, trips the endpoint's own size validation and answers 400
// on every call (defect 3). The export is built client-side instead (FR-039).
export const getProspectAnalyticsList = async (
  query: ProspectListQuery = {}
): Promise<ProspectAnalyticsPage> => {
  const params: Record<string, unknown> = {};
  if (query.page != null) params.page = query.page;
  if (query.size != null) params.size = query.size;
  if (query.sort) params.sort = query.sort;
  if (query.order) params.order = query.order;
  if (query.states?.length) params.state = query.states;
  if (query.customerType) params.type = query.customerType;
  if (query.signMethod) params.sign_method = query.signMethod;
  if (query.leadSource) params.lead_source = query.leadSource;
  if (query.vendor) params.vendor = query.vendor;
  if (query.dateFrom) params.date_from = query.dateFrom;
  if (query.dateTo) params.date_to = query.dateTo;
  if (query.dateField) params.date_field = query.dateField;
  if (query.setupFeeStatus) params.setup_fee_status = query.setupFeeStatus;
  if (query.prospectType) params.prospect_type = query.prospectType;

  const { data } = await accountApi.get(`${BASE}/list`, { params });
  return {
    total: data?.total ?? 0,
    page: data?.page ?? 1,
    size: data?.size ?? 0,
    prospects: (data?.prospects ?? []).map(mapProspectRow),
  };
};

// GET /segmentation/lead-source
export const getLeadSourceSegmentation = async (
  prospectType?: ProspectTypeFilter
): Promise<LeadSourceStat[]> => {
  const { data } = await getSection(`${BASE}/segmentation/lead-source`, {}, prospectType);
  return (data?.segments ?? []).map((s: any) => ({
    source: s.source,
    count: s.count,
    percentage: s.percentage,
  }));
};

const EMPTY_TYPE_STATS = {
  count: 0,
  avgDurationDays: 0,
  conversionRatePct: 0,
};

const mapTypeStats = (t: any) =>
  t
    ? {
        count: t.count,
        avgDurationDays: t.avg_duration_days,
        conversionRatePct: t.conversion_rate_pct,
      }
    : { ...EMPTY_TYPE_STATS };

// GET /segmentation/customer-type
//
// Note the shape: an object with two fixed keys, not an array like its two
// sibling segmentations. Renamed away from `private`, which reads as a keyword.
export const getCustomerTypeSegmentation =
  async (prospectType?: ProspectTypeFilter): Promise<CustomerTypeSegmentation> => {
    const { data } = await getSection(`${BASE}/segmentation/customer-type`, {}, prospectType);
    return {
      privateCustomers: mapTypeStats(data?.private),
      businessCustomers: mapTypeStats(data?.business),
    };
  };

// GET /segmentation/vendor
export const getVendorSegmentation = async (
  prospectType?: ProspectTypeFilter
): Promise<VendorStat[]> => {
  const { data } = await getSection(`${BASE}/segmentation/vendor`, {}, prospectType);
  return (data?.vendors ?? []).map((v: any) => ({
    vendor: v.vendor,
    count: v.count,
    avgSetupDays: v.avg_setup_days,
  }));
};

// GET /alerts — overdue prospects, longest-stuck first, unpaged.
export const getOverdueAlerts = async (
  prospectType?: ProspectTypeFilter
): Promise<OverdueAlert[]> => {
  const { data } = await getSection(`${BASE}/alerts`, {}, prospectType);
  return (data?.alerts ?? []).map((a: any) => ({
    prospectId: a.prospect_id,
    name: a.name,
    state: a.state,
    daysStuck: a.days_stuck,
  }));
};

// GET /volume?months=N — new and completed onboardings per month.
export const getVolume = async (
  months: number,
  prospectType?: ProspectTypeFilter
): Promise<MonthlyVolume[]> => {
  const { data } = await getSection(`${BASE}/volume`, { months }, prospectType);
  return (data?.months ?? []).map((m: any) => ({
    month: m.month,
    newProspects: m.new_prospects,
    completed: m.completed,
  }));
};

// GET /satisfaction?months=N — onboarding ratings of the prospects completed in
// the last N months (CWA-207). The API always sends the keys "1"–"5"; a missing
// one is read as zero rather than leaving a gap in the chart.
export const getSatisfaction = async (
  months: number,
  prospectType?: ProspectTypeFilter
): Promise<OnboardingSatisfaction> => {
  const { data } = await getSection(`${BASE}/satisfaction`, { months }, prospectType);
  const distribution = {} as Record<StarValue, number>;
  STAR_VALUES.forEach((star) => {
    distribution[star] = data?.distribution?.[String(star)] ?? 0;
  });
  return {
    months: data?.months ?? months,
    completedCount: data?.completed_count ?? 0,
    ratedCount: data?.rated_count ?? 0,
    averageStars: data?.average_stars ?? null,
    responseRatePercent: data?.response_rate_percent ?? null,
    distribution,
    comments: (data?.comments ?? []).map((c: any) => ({
      prospectId: c.prospect_id,
      name: c.name,
      stars: c.stars,
      comment: c.comment,
      submittedAt: c.submitted_at,
    })),
  };
};
